import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { CHAT_MAX_LENGTH, type DirectMessage } from '@etown/shared';
import { api, type DirectoryUser } from '../../net/api';
import { useDmStore } from '../../stores/dmStore';
import { usePlayersStore } from '../../stores/playersStore';
import { useSessionStore } from '../../stores/sessionStore';
import { useOffice } from '../office/OfficeContext';

/**
 * Immer dasselbe Objekt fuer "noch nichts geladen".
 *
 * zustand liest den Zustand ueber `useSyncExternalStore`, und das verlangt
 * bei jedem Aufruf dieselbe Referenz. Ein frisches `[]` im Selektor sieht
 * React als staendige Aenderung — die Konversation rendert sich dann in einer
 * Schleife tot, bis der Verlauf da ist. Trifft jede Konversation, die noch
 * keinen Verlauf hat, also jedes neu begonnene Gespraech.
 */
const OHNE_NACHRICHTEN: DirectMessage[] = [];

/** Direktnachrichten-Tab: Konversationsliste bzw. offene Konversation. */
export function DmPanel() {
  const activePeerId = useDmStore((s) => s.activePeerId);
  return activePeerId ? <Conversation peerId={activePeerId} /> : <ConversationList />;
}

function ConversationList() {
  const { socket } = useOffice();
  const conversations = useDmStore((s) => s.conversations);
  const open = useDmStore((s) => s.openConversation);
  const [picking, setPicking] = useState(false);

  const startWith = (peerId: string, displayName?: string): void => {
    open(peerId, displayName);
    socket.emit('dm:read', { peerUserId: peerId }, () => undefined);
  };

  if (picking) {
    return (
      <UserPicker
        onPick={(id, name) => {
          setPicking(false);
          startWith(id, name);
        }}
        onCancel={() => setPicking(false)}
      />
    );
  }

  return (
    <div className="dm">
      <button className="dm__new" onClick={() => setPicking(true)}>
        ＋ Neue Nachricht
      </button>
      {conversations.length === 0 && <p className="chat__empty">Noch keine Direktnachrichten.</p>}
      <ul className="dm__list">
        {conversations.map((conv) => (
          <li key={conv.peerId}>
            <button className="dm__conv" onClick={() => startWith(conv.peerId, conv.peerDisplayName)}>
              <span className="dm__conv-name">{conv.peerDisplayName}</span>
              <span className="dm__conv-preview">{conv.lastBody}</span>
              {conv.unread > 0 && <span className="dm__conv-badge">{conv.unread}</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function UserPicker({
  onPick,
  onCancel,
}: {
  onPick: (id: string, displayName: string) => void;
  onCancel: () => void;
}) {
  const selfUserId = useSessionStore((s) => s.user?.id);
  // Das Set entsteht beim Rendern, nicht im Selektor: `useSyncExternalStore`
  // verlangt bei jedem Lesen dieselbe Referenz, und ein frisches Set sieht
  // React als staendige Aenderung — die Liste rendert sich sonst in einer
  // Schleife tot, statt Nutzer anzuzeigen. Siehe OHNE_NACHRICHTEN oben.
  const roster = usePlayersStore((s) => s.roster);
  const onlineUserIds = useMemo(() => new Set(roster.map((e) => e.userId)), [roster]);
  const [users, setUsers] = useState<DirectoryUser[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .listUsers()
      .then(({ users }) => {
        if (!cancelled) setUsers(users.filter((u) => u.id !== selfUserId));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Konnte Nutzer nicht laden');
      });
    return () => {
      cancelled = true;
    };
  }, [selfUserId]);

  return (
    <div className="dm">
      <button className="dm__back" onClick={onCancel}>
        ‹ Zurück
      </button>
      {error && <p className="chat__error">{error}</p>}
      <ul className="dm__list">
        {users.map((user) => (
          <li key={user.id}>
            <button className="dm__conv" onClick={() => onPick(user.id, user.displayName)}>
              <span className="dm__conv-name">{user.displayName}</span>
              <span className={`dm__status ${onlineUserIds.has(user.id) ? 'dm__status--online' : ''}`}>
                {onlineUserIds.has(user.id) ? 'online' : 'offline'}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Conversation({ peerId }: { peerId: string }) {
  const { socket } = useOffice();
  const selfUserId = useSessionStore((s) => s.user?.id);
  const messages = useDmStore((s) => s.messagesByPeer[peerId] ?? OHNE_NACHRICHTEN);
  const hasMore = useDmStore((s) => s.hasMoreByPeer[peerId] ?? false);
  const conversation = useDmStore((s) => s.conversations.find((c) => c.peerId === peerId));
  // Ein frisch begonnenes Gespraech steht in keiner Konversationsliste — dann
  // zaehlt der Name, mit dem es geoeffnet wurde
  const geoeffnetAls = useDmStore((s) => s.activePeerName);
  const close = useDmStore((s) => s.closeConversation);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const peerName = conversation?.peerDisplayName ?? messages[0]?.fromDisplayName ?? geoeffnetAls ?? 'Direktnachricht';

  // Verlauf laden und als gelesen markieren, sobald die Konversation offen ist
  useEffect(() => {
    socket.emit('dm:history', { peerUserId: peerId }, (res) => {
      if (res.ok) useDmStore.getState().setHistory(peerId, res.data.messages, res.data.hasMore);
    });
    socket.emit('dm:read', { peerUserId: peerId }, () => undefined);
    // Konversationsliste lokal auf gelesen setzen
    useDmStore.setState((state) => ({
      conversations: state.conversations.map((c) => (c.peerId === peerId ? { ...c, unread: 0 } : c)),
    }));
  }, [peerId, socket]);

  useEffect(() => {
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, [messages]);

  const loadOlder = (): void => {
    const oldest = messages[0];
    socket.emit('dm:history', { peerUserId: peerId, beforeId: oldest?.id }, (res) => {
      if (res.ok) useDmStore.getState().prependHistory(peerId, res.data.messages, res.data.hasMore);
    });
  };

  const send = (event: FormEvent): void => {
    event.preventDefault();
    const body = draft.trim();
    if (!body) return;
    setError(null);
    socket.emit('dm:send', { toUserId: peerId, body }, (res) => {
      if (res.ok) {
        useDmStore.getState().appendOwn(res.data);
        setDraft('');
      } else {
        setError(res.error);
      }
    });
  };

  return (
    <div className="dm">
      <button className="dm__back" onClick={close}>
        ‹ {peerName}
      </button>
      <div ref={listRef} className="chat__list dm__messages">
        {hasMore && (
          <button className="chat__more" onClick={loadOlder}>
            Ältere Nachrichten laden
          </button>
        )}
        {messages.length === 0 && <p className="chat__empty">Noch keine Nachrichten.</p>}
        {messages.map((message) => (
          <div
            key={message.id}
            className={`dm__message ${message.fromUserId === selfUserId ? 'dm__message--own' : ''}`}
          >
            <p className="chat__body">{message.body}</p>
            <span className="chat__time">
              {new Date(message.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        ))}
      </div>
      {error && <p className="chat__error" role="alert">{error}</p>}
      <form className="chat__form" onSubmit={send}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          maxLength={CHAT_MAX_LENGTH}
          placeholder={`Nachricht an ${peerName}…`}
        />
        <button type="submit" disabled={!draft.trim()}>
          Senden
        </button>
      </form>
    </div>
  );
}
